import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CwKHttpErrorResponse } from '@shared/types/cwk-http-error-response.type';

export const ErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

  const platformId = inject(PLATFORM_ID);

  return next(req).pipe(
    catchError((error: unknown) => {

      // We only care about HttpErrorResponse
      if (error instanceof HttpErrorResponse) {
        const cwkError = error as CwKHttpErrorResponse;
        cwkError.FriendlyMessage = getErrorMessage(error, platformId);
        console.error('HTTP Error:', cwkError); // Log the full error for developers
        return throwError(() => cwkError);
      }

      // Re-throw the error so component-level error handlers can still catch it
      return throwError(() => error);
    })
  );
};

// Helper function to parse the error
function getErrorMessage(error: HttpErrorResponse, platformId: object): string {

  // A network error occurred (e.g., DNS resolution, offline)
  if (isNetworkError(error, platformId)) {
    return 'Network error. Please check your connection.';
  }

  const extractedErrorMessage = extractErrorMessage(error.error);
  if (extractedErrorMessage) {
    return extractedErrorMessage;
  }

  if (error.status) {
    if (error.statusText && error.statusText !== 'Unknown Error') {
      return `${error.status} ${error.statusText}`;
    }
    return `Server Error: ${error.status}`;
  }

  // Fallback to the status text
  return error.statusText || 'An unknown error occurred.';
}

function extractErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || null;
  }

  if (Array.isArray(value)) {
    const messages = value
      .map(item => extractErrorMessage(item))
      .filter((item): item is string => !!item);

    return messages.length ? messages.join('; ') : null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const errorRecord = value as Record<string, unknown>;

  // Angular wraps non-JSON text responses in an object that includes the raw body.
  if (isJsonParseFailure(errorRecord)) {
    return (errorRecord['text'] as string).trim();
  }

  for (const key of ['FriendlyMessage', 'message', 'error', 'detail', 'title']) {
    const message = extractErrorMessage(errorRecord[key]);
    if (message) {
      return message;
    }
  }

  const validationMessage = extractValidationMessage(errorRecord['errors']);
  if (validationMessage) {
    return validationMessage;
  }

  const textMessage = extractErrorMessage(errorRecord['text']);
  if (textMessage) {
    return textMessage;
  }

  return null;
}

function extractValidationMessage(value: unknown): string | null {
  if (Array.isArray(value)) {
    return extractErrorMessage(value);
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const validationRecord = value as Record<string, unknown>;
  const messages = Object.entries(validationRecord)
    .map(([field, fieldErrors]) => {
      const fieldMessage = extractErrorMessage(fieldErrors);
      return fieldMessage ? `${field}: ${fieldMessage}` : null;
    })
    .filter((item): item is string => !!item);

  return messages.length ? messages.join('; ') : null;
}

function isJsonParseFailure(errorRecord: Record<string, unknown>): boolean {
  if (typeof errorRecord['text'] !== 'string' || !errorRecord['text'].trim()) {
    return false;
  }

  return errorRecord['error'] instanceof SyntaxError
    || (typeof errorRecord['message'] === 'string' && errorRecord['message'].includes('Http failure during parsing'));
}

function isNetworkError(error: HttpErrorResponse, platformId: object): boolean {
  if (error.status === 0) {
    return true;
  }

  if (!isPlatformBrowser(platformId)) {
    return false;
  }

  return error.error instanceof ErrorEvent
    || (typeof ProgressEvent !== 'undefined' && error.error instanceof ProgressEvent)
    || (typeof TypeError !== 'undefined' && error.error instanceof TypeError);
}
